/**
 * Keeps a column added to a management table visible by default for users who
 * already have a saved column configuration.
 *
 * `useTableManageColumns` persists the visible columns under
 * `<name>_manageColumns_showHide_state`. A column added to HEADERS afterwards
 * is absent from that list, so for anyone who had already opened the screen it
 * renders hidden until they find it in the column picker — which is how the
 * ADA and Accessibility Type columns went unseen. The library only restores a
 * column when `initialSelectedColumns` and `forceOverwrite` are passed
 * together, so the two are always returned as a pair.
 *
 * A column the user deliberately hid is also absent from the saved list. The
 * two cases are told apart with a second entry,
 * `<name>_manageColumns_knownColumns`: every column id the table has rendered
 * for this user so far. A header missing from it is new and joins the visible
 * set once; a header on it stays however the user left it.
 *
 * Before the known list exists nothing can be told apart, so the first run
 * only surfaces the columns the table names as `introducedColumnIds` — the
 * ones that shipped before this migration existed. Anything else the user had
 * hidden (amenity fees on Rent Roll, say) stays hidden. From then on the known
 * list handles every later addition on its own.
 */

const showHideKey = (name) => `${name}_manageColumns_showHide_state`;
const columnOrderKey = (name) => `${name}_column-order`;
export const knownColumnsKey = (name) => `${name}_manageColumns_knownColumns`;

const KEEP_USER_CHOICES = {
  initialSelectedColumns: undefined,
  shouldForceOverwrite: false,
};

const readStoredIds = (key) => {
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    return Array.isArray(stored) ? stored : null;
  } catch {
    // Unreadable or corrupt: the library falls back to its own defaults.
    return null;
  }
};

export const getNewColumnsVisibilityState = ({
  headers = [],
  name = '',
  introducedColumnIds = [],
}: {
  headers: Array<Object>,
  name: string,
  introducedColumnIds?: Array<string>,
}): {
  initialSelectedColumns: Array<string> | void,
  shouldForceOverwrite: boolean,
} => {
  const columnIds = headers.map(({ id }) => id);
  if (!columnIds.length) return KEEP_USER_CHOICES;

  const visibleColumnIds = readStoredIds(showHideKey(name));
  if (!visibleColumnIds?.length) {
    // Nothing saved yet (or everything hidden, which the library already
    // resets on load): its defaults show every column.
    return KEEP_USER_CHOICES;
  }

  const knownColumnIds = readStoredIds(knownColumnsKey(name));
  const unseenColumnIds = knownColumnIds
    ? columnIds.filter((id) => !knownColumnIds.includes(id))
    : columnIds.filter((id) => introducedColumnIds.includes(id));
  const newColumnIds = unseenColumnIds.filter(
    (id) => !visibleColumnIds.includes(id),
  );
  if (!newColumnIds.length) return KEEP_USER_CHOICES;

  // Appended rather than rebuilt from `columnIds`: the saved list can name
  // columns a feature flag currently removes from HEADERS, and dropping them
  // would hide those columns when the flag comes back.
  return {
    initialSelectedColumns: [...visibleColumnIds, ...newColumnIds],
    shouldForceOverwrite: true,
  };
};

/**
 * The stored `${name}_column-order` map as it is, or null when there is none
 * or it cannot be read. `forceOverwrite` rewrites the column order as well as
 * the visibility, so a caller forcing only for the visibility passes this back
 * as `initialColumnsOrder` and the order write becomes a no-op.
 */
export const readStoredColumnOrder = (name: string): ?Object => {
  try {
    const stored = JSON.parse(localStorage.getItem(columnOrderKey(name)));
    return stored && typeof stored === 'object' && !Array.isArray(stored)
      ? stored
      : null;
  } catch {
    return null;
  }
};

/**
 * Records the current headers as seen, so a later addition can be told apart
 * from a column the user hides in the meantime. Ids are only ever added: a
 * column a feature flag removes and later restores is not new the second time.
 */
export const rememberColumns = ({
  headers = [],
  name = '',
}: {
  headers: Array<Object>,
  name: string,
}) => {
  const columnIds = headers.map(({ id }) => id);
  if (!columnIds.length) return;

  const knownColumnIds = readStoredIds(knownColumnsKey(name)) ?? [];
  const unknownColumnIds = columnIds.filter(
    (id) => !knownColumnIds.includes(id),
  );
  if (!unknownColumnIds.length) return;

  try {
    localStorage.setItem(
      knownColumnsKey(name),
      JSON.stringify([...knownColumnIds, ...unknownColumnIds]),
    );
  } catch {
    // Storage unavailable: the next visit tries again.
  }
};
